import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, size, useFloating } from "@floating-ui/react-dom";
import usePopoverDismiss from "../../hooks/usePopoverDismiss.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";
import { SECONDARY } from "./Fields.jsx";
import { HoverCard } from "../resultsPrimitives/hoverCardPrimitives.jsx";

export default function ConsolidationPopover({ label, title = label, tooltip, icon: Icon, children, className = SECONDARY, panelWidth = "38rem", dismissible = true, disabled = false, pressed, closeLabel = "Fermer" }) {
  const [open, setOpen] = React.useState(false);
  const trigger = React.useRef(null);
  const closeButton = React.useRef(null);
  const id = React.useId();
  // Position fixe : le panneau suit son bouton même si la page change de hauteur pendant qu'il est ouvert.
  const { refs, floatingStyles, isPositioned } = useFloating({ open, placement: "bottom-start", strategy: "fixed",
    middleware: [offset(6), flip({ padding: 12 }), shift({ padding: 12 }), size({ padding: 12,
      apply({ availableWidth, availableHeight, elements }) {
        Object.assign(elements.floating.style, { maxWidth: `${Math.max(0, availableWidth)}px`, maxHeight: `${Math.max(0, availableHeight)}px` });
      },
    })], whileElementsMounted: autoUpdate });
  const close = () => { setOpen(false); trigger.current?.focus({ preventScroll: true }); };
  usePopoverDismiss({ anchorElement: trigger.current, floatingRef: refs.floating, isOpen: open && dismissible, onClose: close });
  React.useEffect(() => {
    if (open && isPositioned) closeButton.current?.focus({ preventScroll: true });
  }, [open, isPositioned]);
  const button = <button ref={node => { trigger.current = node; refs.setReference(node); }} type="button"
      className={className} disabled={disabled} aria-pressed={pressed} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => setOpen(value => !value)}>
      {Icon ? <Icon size={14} className="shrink-0" aria-hidden="true" /> : null}<span>{label}</span>
    </button>;
  return <>
    {tooltip ? <HoverCard content={tooltip} referenceAsChild disabled={open} closeOnReferenceClick flat compactTitle tight>{button}</HoverCard> : button}
    {open ? createPortal(<div ref={refs.setFloating} id={id} role="dialog" aria-modal="false" aria-labelledby={`${id}-title`}
      style={{ ...floatingStyles, width: `min(${panelWidth}, calc(100vw - 1.5rem))`, visibility: isPositioned ? "visible" : "hidden" }}
      className="z-popover grid max-h-[calc(100dvh-1.5rem)] gap-2 overflow-y-auto rounded-[4px] border border-slate-300 bg-white p-3 text-slate-700 shadow-xl">
      <div className="flex items-start justify-between gap-2">
        <h2 id={`${id}-title`} className="text-sm font-semibold">{title}</h2>
        <button ref={closeButton} type="button" className={SECONDARY} onClick={close}>{closeLabel}</button>
      </div>
      {typeof children === "function" ? children({ close }) : children}
    </div>, getReactPortalRoot()) : null}
  </>;
}
